import { Box, Button, Stack, Typography } from "@mui/material";
import { DoNotDisturbOutlined } from "@mui/icons-material";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import strings from "global/constants/StringConstants";
import { ModalLockIcon } from "global/components/AppModal";
import {
  actionRowSx,
  ghostActionSx,
  primaryActionSx,
} from "global/components/EmptyState/EmptyState.styles";
import featureGateTokens from "./FeatureGate.tokens";

interface CustomProps {
  status: "not-in-plan" | "admin-disabled";
  disabledMessage?: string;
  variant?: "full" | "inline";
  /** Display name of the gated feature, e.g. "Bulk send". Shown as a small
   *  header row + status pill above the card when supplied; omitted entirely
   *  otherwise so existing call sites render unchanged. */
  featureName?: string;
}

const t = featureGateTokens;

const FeatureGateBlock = (props: CustomProps) => {
  const isUpgrade = props.status === "not-in-plan";
  const isInline = props.variant === "inline";
  const tone = t.tone[isUpgrade ? "upgrade" : "off"];

  const title = isUpgrade
    ? strings.FEATURE_GATE_UPGRADE_TITLE
    : strings.FEATURE_GATE_MAINTENANCE_TITLE;
  const description = isUpgrade
    ? isInline
      ? strings.FEATURE_GATE_UPGRADE_DESCRIPTION_INLINE
      : strings.FEATURE_GATE_UPGRADE_DESCRIPTION_FULL
    : (props.disabledMessage ?? strings.FEATURE_GATE_FALLBACK_MAINTENANCE_MESSAGE);

  const handleUpgrade = () => {
    history.push(viewpaths.viewMyPlanViewPath);
  };

  const tileSize = isInline ? 40 : t.layout.tileSize;

  const card = (
    <Box
      sx={{
        width: "100%",
        boxSizing: "border-box",
        maxWidth: isInline ? 360 : undefined,
        border: `1px solid ${tone.border}`,
        borderRadius: t.radius.cardOuter,
        overflow: "hidden",
        bgcolor: "background.paper",
        ...(!isInline && { display: "flex", flexDirection: "column" }),
      }}
    >
      {props.featureName && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${t.color.hairline}`,
          }}
        >
          <Typography sx={{ fontSize: "13.5px", fontWeight: 800, color: t.color.ink }}>
            {props.featureName}
          </Typography>
          <Box
            component="span"
            sx={{
              fontSize: "9.5px",
              fontWeight: 800,
              letterSpacing: ".08em",
              color: tone.text,
              bgcolor: tone.badgeBg,
              px: "9px",
              py: "3px",
              borderRadius: t.radius.badge,
            }}
          >
            {isUpgrade ? "UPGRADE" : "OFF"}
          </Box>
        </Box>
      )}

      <Stack
        alignItems="center"
        // The full variant spaces itself with the empty state's `gap`; MUI's
        // `spacing` would add margins on top of it.
        spacing={isInline ? 1.25 : 0}
        sx={{
          textAlign: "center",
          bgcolor: t.color.innerSurface,
          ...(isInline
            ? { px: 2.5, py: 3.5 }
            : {
                px: t.layout.padX,
                py: t.layout.padY,
                gap: t.layout.gap,
                [t.layout.phoneQuery]: {
                  px: t.layout.phonePadX,
                  py: t.layout.phonePadY,
                  gap: t.layout.phoneGap,
                },
              }),
        }}
      >
        <Box
          sx={{
            width: tileSize,
            height: tileSize,
            flexShrink: 0,
            boxSizing: "border-box",
            borderRadius: isInline ? t.radius.tileLg : t.layout.tileRadius,
            bgcolor: tone.tile,
            ...(!isInline && {
              [t.layout.phoneQuery]: {
                width: t.layout.tileSizePhone,
                height: t.layout.tileSizePhone,
                borderRadius: t.layout.tileRadiusPhone,
              },
            }),
            color: tone.icon,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isUpgrade ? (
            <ModalLockIcon size={isInline ? 18 : 22} />
          ) : (
            <DoNotDisturbOutlined sx={{ fontSize: isInline ? 18 : 22 }} />
          )}
        </Box>

        <Stack alignItems="center" spacing={0.5}>
          {/* The full-page variant is the whole screen, so its title is the
              document's h1; the inline variant sits inside a screen that
              already owns the h1, so it's an h2 instead (WCAG 1.3.1). */}
          <Typography
            component={isInline ? "h2" : "h1"}
            sx={{
              fontSize: isInline ? "13.5px" : t.layout.titleFontSize,
              fontWeight: 800,
              color: t.color.ink,
              ...(!isInline && {
                [t.layout.phoneQuery]: {
                  fontSize: t.layout.titleFontSizePhone,
                },
              }),
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: "12.5px",
              lineHeight: isInline ? 1.6 : t.layout.bodyLineHeight,
              color: t.color.muted,
              maxWidth: isInline ? 360 : t.layout.bodyMaxWidth,
            }}
          >
            {description}
          </Typography>
        </Stack>

        {isUpgrade &&
          (isInline ? (
            <Stack direction="row" spacing={1.25} sx={{ pt: 0.5 }}>
              <Button
                variant="contained"
                onClick={handleUpgrade}
                sx={{ px: 2.5, py: 1, fontWeight: 700 }}
              >
                {strings.FEATURE_GATE_VIEW_PLANS_BUTTON}
              </Button>
            </Stack>
          ) : (
            // The empty state's own action row, not a copy of it — same gap,
            // same button sizes, and the same stack-and-flip below 600px.
            <Box sx={actionRowSx}>
              <Button onClick={handleUpgrade} sx={ghostActionSx}>
                {strings.FEATURE_GATE_COMPARE_PLANS_BUTTON}
              </Button>
              <Button
                variant="contained"
                onClick={handleUpgrade}
                sx={primaryActionSx}
              >
                {strings.FEATURE_GATE_UPGRADE_PLAN_BUTTON}
              </Button>
            </Box>
          ))}
      </Stack>
    </Box>
  );

  if (isInline) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ width: "100%", height: "100%", minHeight: 220, px: 2, py: 3, flex: 1 }}
      >
        {card}
      </Stack>
    );
  }

  // The full-page variant sits in the page's own layout (below its normal
  // header row, inside whatever gutter the screen already applies) rather
  // than taking over the viewport — it replaces the page's content, not the
  // whole app shell. It hugs its content, the same as the framed empty state
  // it stands in for; a 420px floor made a one-line message fill the screen.
  return <Box sx={{ width: "100%", flex: "0 0 auto" }}>{card}</Box>;
};

export default FeatureGateBlock;
